import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, queryString } from '../api';
import { useAuth } from '../auth';
import {
  EmptyState,
  ErrorNotice,
  Spinner,
  StatusPill,
  formatDate,
  fullName,
} from '../components/ui';
import { ALL_STATUSES, type MemberList, type MemberStatus } from '../types';

const PER_PAGE = 25;

/**
 * Search state lives in the URL, not in component state.
 *
 * It costs nothing to do it this way from the start and it buys two things the
 * committee will actually use: the back button behaves, and the secretary can
 * send someone a link to a filtered list rather than describing which boxes to
 * tick.
 */
export function MembersPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const status = (params.get('status') ?? '') as MemberStatus | '';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const [draft, setDraft] = useState(query);
  const [data, setData] = useState<MemberList | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const firstRender = useRef(true);

  // Keep the box in step when the URL changes underneath us — a back button
  // press, or a link someone was sent.
  useEffect(() => {
    setDraft(query);
  }, [query]);

  // Debounce typing into the URL. Replace rather than push, so a search does
  // not leave twelve entries in the history for twelve keystrokes.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (draft === query) return;

    const timer = window.setTimeout(() => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (draft === '') next.delete('q');
          else next.set('q', draft);
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    }, 250);

    return () => window.clearTimeout(timer);
  }, [draft, query, setParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .get<MemberList>(
        `/members${queryString({ q: query, status, page, per_page: PER_PAGE })}`,
      )
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((failure) => {
        if (!cancelled) setError(failure);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, status, page]);

  function setStatus(next: MemberStatus | '') {
    setParams((previous) => {
      const updated = new URLSearchParams(previous);
      if (next === '') updated.delete('status');
      else updated.set('status', next);
      updated.delete('page');
      return updated;
    });
  }

  function goToPage(next: number) {
    setParams((previous) => {
      const updated = new URLSearchParams(previous);
      if (next <= 1) updated.delete('page');
      else updated.set('page', String(next));
      return updated;
    });
    window.scrollTo({ top: 0 });
  }

  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <>
      <div className="page-head">
        <h1>Members</h1>
        <div className="head-actions">
          <span className="muted">
            {loading && data === null
              ? 'Loading…'
              : `${total} ${total === 1 ? 'member' : 'members'}${
                  query || status ? ' matching' : ''
                }`}
          </span>
          {can('members_edit') ? (
            <>
              <Link to="/members/new" className="button-link">
                Add a member
              </Link>
              <Link to="/members/import" className="button-link">
                Import a list
              </Link>
            </>
          ) : null}
        </div>
      </div>

      <div className="filters">
        <div className="field search">
          <label htmlFor="member-search">Search</label>
          <input
            id="member-search"
            type="search"
            placeholder="Name, email, postcode or BBKA number"
            value={draft}
            autoFocus
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>

        {/* Buttons rather than a dropdown: one click instead of three, and you
            can see what is currently applied without opening anything. */}
        <div className="chips" role="group" aria-label="Filter by status">
          <button
            type="button"
            className={status === '' ? 'chip chip-on' : 'chip'}
            aria-pressed={status === ''}
            onClick={() => setStatus('')}
          >
            All
          </button>
          {ALL_STATUSES.map((value) => (
            <button
              key={value}
              type="button"
              className={status === value ? 'chip chip-on' : 'chip'}
              aria-pressed={status === value}
              onClick={() => setStatus(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <ErrorNotice error={error} />

      {loading && data === null ? <Spinner label="Loading members" /> : null}

      {data !== null && data.rows.length === 0 ? (
        <EmptyState title="No members match that search">
          Try a shorter search, or clear the status filter.
        </EmptyState>
      ) : null}

      {data !== null && data.rows.length > 0 ? (
        <>
          <table className="members">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Where</th>
                <th scope="col">Contact</th>
                <th scope="col">Status</th>
                <th scope="col">Joined</th>
                <th scope="col">Last signed in</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((member) => (
                <tr key={member.id}>
                  <td data-label="Name">
                    <Link to={`/members/${member.id}`} className="member-name">
                      {fullName(member)}
                    </Link>
                    <span className="badges">
                      {member.is_mentor ? <span className="badge">mentor</span> : null}
                      {!member.has_login ? (
                        <span className="badge badge-quiet" title="No online account">
                          no login
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td data-label="Where">
                    {member.town ?? '—'}
                    {member.postcode ? <span className="muted"> {member.postcode}</span> : null}
                  </td>
                  <td data-label="Contact">
                    {member.email ? (
                      <a href={`mailto:${member.email}`}>{member.email}</a>
                    ) : (
                      <span className="muted">No email address</span>
                    )}
                    {member.telephone || member.mobile ? (
                      <span className="muted block">{member.telephone ?? member.mobile}</span>
                    ) : null}
                  </td>
                  <td data-label="Status">
                    <StatusPill status={member.status} />
                  </td>
                  <td data-label="Joined">{formatDate(member.joined_date)}</td>
                  <td data-label="Last signed in">
                    {!member.has_login ? (
                      <span className="muted">—</span>
                    ) : member.last_login_at ? (
                      formatDate(member.last_login_at)
                    ) : (
                      <span className="muted">Never</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {lastPage > 1 ? (
            <nav className="pager" aria-label="Pages">
              <button type="button" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                ← Previous
              </button>
              <span className="muted">
                Page {page} of {lastPage}
              </span>
              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= lastPage}
              >
                Next →
              </button>
            </nav>
          ) : null}
        </>
      ) : null}
    </>
  );
}
